/**
 * Oryx Theme Resolver
 * Converts dungeon theme strings to actual tile frame numbers
 *
 * This bridges the gap between dungeon JSON data (which uses string themes like 'wood', 'stone')
 * and the WORLD_TILES/MONSTERS constants (which are frame indices)
 */

import { WORLD_TILES, MONSTERS, randomElement } from './tileIndex';

// Re-export for convenience
export { MONSTERS };

// ============================================================================
// Types
// ============================================================================

export interface ResolvedDungeonVisuals {
  floorTiles: number[];
  wallTilesH: number[];
  wallTilesV: number[];
  monsterFrame: number;
  bossFrame: number;
}

export interface PropConfig {
  frame: number;
  animationKey?: string;
}

// ============================================================================
// Floor Theme Mappings
// ============================================================================

const FLOOR_THEMES: Record<string, number[]> = {
  wood: [WORLD_TILES.FLOOR_WOOD_A, WORLD_TILES.FLOOR_WOOD_B, WORLD_TILES.FLOOR_WOOD_C, WORLD_TILES.FLOOR_WOOD_D],
  stone: [WORLD_TILES.FLOOR_STONE_A, WORLD_TILES.FLOOR_STONE_B, WORLD_TILES.FLOOR_STONE_C, WORLD_TILES.FLOOR_STONE_D],
  tile: [WORLD_TILES.FLOOR_TILE_A, WORLD_TILES.FLOOR_TILE_B, WORLD_TILES.FLOOR_TILE_C, WORLD_TILES.FLOOR_TILE_D],
  carpet: [WORLD_TILES.FLOOR_CARPET_A, WORLD_TILES.FLOOR_CARPET_B, WORLD_TILES.FLOOR_CARPET_C],
  mossy: [WORLD_TILES.FLOOR_MOSSY_A, WORLD_TILES.FLOOR_MOSSY_B, WORLD_TILES.FLOOR_MOSSY_C],
  grass: [WORLD_TILES.FLOOR_GRASS_A, WORLD_TILES.FLOOR_GRASS_B, WORLD_TILES.FLOOR_GRASS_C],
  dirt: [WORLD_TILES.FLOOR_DIRT_A, WORLD_TILES.FLOOR_DIRT_B, WORLD_TILES.FLOOR_DIRT_C],
  cobble: [WORLD_TILES.FLOOR_COBBLE_A, WORLD_TILES.FLOOR_COBBLE_B, WORLD_TILES.FLOOR_COBBLE_C],
};

// ============================================================================
// Wall Theme Mappings
// ============================================================================

const WALL_THEMES_H: Record<string, number[]> = {
  stone: [WORLD_TILES.WALL_STONE_H_A, WORLD_TILES.WALL_STONE_H_B, WORLD_TILES.WALL_STONE_H_C],
  cave: [WORLD_TILES.WALL_CAVE_H_A, WORLD_TILES.WALL_CAVE_H_B, WORLD_TILES.WALL_CAVE_H_C],
  crypt: [WORLD_TILES.WALL_CRYPT_H_A, WORLD_TILES.WALL_CRYPT_H_B, WORLD_TILES.WALL_CRYPT_H_C],
  sewer: [WORLD_TILES.WALL_SEWER_H_A, WORLD_TILES.WALL_SEWER_H_B, WORLD_TILES.WALL_SEWER_H_C],
};

const WALL_THEMES_V: Record<string, number[]> = {
  stone: [WORLD_TILES.WALL_STONE_V_A, WORLD_TILES.WALL_STONE_V_B, WORLD_TILES.WALL_STONE_V_C],
  cave: [WORLD_TILES.WALL_CAVE_V_A, WORLD_TILES.WALL_CAVE_V_B, WORLD_TILES.WALL_CAVE_V_C],
  crypt: [WORLD_TILES.WALL_CRYPT_V_A, WORLD_TILES.WALL_CRYPT_V_B, WORLD_TILES.WALL_CRYPT_V_C],
  sewer: [WORLD_TILES.WALL_SEWER_V_A, WORLD_TILES.WALL_SEWER_V_B, WORLD_TILES.WALL_SEWER_V_C],
};

// ============================================================================
// Monster Mappings
// ============================================================================

export const MONSTER_MAPPING: Record<string, number> = {
  wizard: MONSTERS.WIZARD,
  necromancer: MONSTERS.NECROMANCER,
  zombie: MONSTERS.ZOMBIE,
  spider: MONSTERS.SPIDER,
  demon: MONSTERS.DEMON,
  dragon: MONSTERS.DRAGON,
  minotaur: MONSTERS.MINOTAUR,
  giant: MONSTERS.GIANT,
  dwarf: MONSTERS.DWARF,
  king: MONSTERS.KING,
  queen: MONSTERS.QUEEN,
  bat: MONSTERS.BAT,
  beholder: MONSTERS.BEHOLDER,
  pixie: MONSTERS.PIXIE,
  satyr: MONSTERS.SATYR,
  centaur: MONSTERS.CENTAUR,
  rat: MONSTERS.RAT,
  treant: MONSTERS.TREANT,
  slime: MONSTERS.SLIME,
  jelly: MONSTERS.JELLY,
  wolf: MONSTERS.WOLF,
  troll: MONSTERS.TROLL,
  mummy: MONSTERS.MUMMY,
  wraith: MONSTERS.WRAITH,
  reaper: MONSTERS.REAPER,
  vampire: MONSTERS.VAMPIRE,
  merchant: MONSTERS.MERCHANT,
  knight: MONSTERS.KNIGHT,
  rogue: MONSTERS.ROGUE,
  archer: MONSTERS.ARCHER,
  paladin: MONSTERS.PALADIN,
  cleric: MONSTERS.CLERIC,
  berserker: MONSTERS.BERSERKER,
  druid: MONSTERS.DRUID,
  fighter: MONSTERS.FIGHTER,
};

// ============================================================================
// Prop Mappings
// ============================================================================

const PROP_CONFIGS: Record<string, PropConfig> = {
  bookshelf: { frame: WORLD_TILES.BOOKSHELF_A },
  bookshelf_a: { frame: WORLD_TILES.BOOKSHELF_A },
  bookshelf_b: { frame: WORLD_TILES.BOOKSHELF_B },
  table: { frame: WORLD_TILES.TABLE },
  chair: { frame: WORLD_TILES.CHAIR },
  candles: { frame: WORLD_TILES.CANDLES_A, animationKey: 'candle-flicker' },
  candles_a: { frame: WORLD_TILES.CANDLES_A, animationKey: 'candle-flicker' },
  candles_b: { frame: WORLD_TILES.CANDLES_B, animationKey: 'candle-flicker' },
  candles_c: { frame: WORLD_TILES.CANDLES_C, animationKey: 'candle-flicker' },
  torch: { frame: WORLD_TILES.TORCH_1, animationKey: 'torch-flicker' },
  skull: { frame: WORLD_TILES.SKULL },
  bones: { frame: WORLD_TILES.BONES_A },
  bones_a: { frame: WORLD_TILES.BONES_A },
  bones_b: { frame: WORLD_TILES.BONES_B },
  blood: { frame: WORLD_TILES.BLOOD_A },
  blood_a: { frame: WORLD_TILES.BLOOD_A },
  blood_b: { frame: WORLD_TILES.BLOOD_B },
  blood_c: { frame: WORLD_TILES.BLOOD_C },
  cobweb: { frame: WORLD_TILES.COBWEB_A },
  cobweb_a: { frame: WORLD_TILES.COBWEB_A },
  cobweb_b: { frame: WORLD_TILES.COBWEB_B },
  chest: { frame: WORLD_TILES.CHEST_CLOSED },
  chest_closed: { frame: WORLD_TILES.CHEST_CLOSED },
  chest_open: { frame: WORLD_TILES.CHEST_OPEN },
  barrel: { frame: WORLD_TILES.BARREL },
  crate: { frame: WORLD_TILES.CRATE },
  bed: { frame: WORLD_TILES.BED },
  throne: { frame: WORLD_TILES.THRONE },
  banner: { frame: WORLD_TILES.BANNER_A },
  banner_a: { frame: WORLD_TILES.BANNER_A },
  banner_b: { frame: WORLD_TILES.BANNER_B },
  flag: { frame: WORLD_TILES.FLAG },
  statue: { frame: WORLD_TILES.STATUE },
  fountain: { frame: WORLD_TILES.FOUNTAIN },
  well: { frame: WORLD_TILES.WELL },
  gravestone: { frame: WORLD_TILES.GRAVESTONE_A },
  gravestone_a: { frame: WORLD_TILES.GRAVESTONE_A },
  gravestone_b: { frame: WORLD_TILES.GRAVESTONE_B },
  mushroom: { frame: WORLD_TILES.MUSHROOM_A },
  mushroom_a: { frame: WORLD_TILES.MUSHROOM_A },
  mushroom_b: { frame: WORLD_TILES.MUSHROOM_B },
  vines: { frame: WORLD_TILES.VINES },
  crystal: { frame: WORLD_TILES.CRYSTAL },
  altar: { frame: WORLD_TILES.ALTAR },
  cauldron: { frame: WORLD_TILES.CAULDRON_1 },
  campfire: { frame: WORLD_TILES.CAMPFIRE_1 },
  rug: { frame: WORLD_TILES.RUG_A },
  rug_a: { frame: WORLD_TILES.RUG_A },
  rug_b: { frame: WORLD_TILES.RUG_B },
  window: { frame: WORLD_TILES.WINDOW_A },
  window_a: { frame: WORLD_TILES.WINDOW_A },
  window_b: { frame: WORLD_TILES.WINDOW_B },
  sign: { frame: WORLD_TILES.SIGN },
  mirror: { frame: WORLD_TILES.MIRROR },
  orb: { frame: WORLD_TILES.ORB },
  scroll: { frame: WORLD_TILES.BOOKSHELF_B },
  pit: { frame: WORLD_TILES.PIT },
  spikes: { frame: WORLD_TILES.SPIKE_TRAP },
  lever: { frame: WORLD_TILES.LEVER_OFF },
  crystal_green: { frame: WORLD_TILES.CRYSTAL },
};

// ============================================================================
// Resolver Functions
// ============================================================================

/**
 * Dungeon theme data as stored in JSON
 */
interface DungeonTheme {
  floor: string;
  wall: string;
  props: string[];
  monster: string;
  boss: string;
}

/**
 * Resolve dungeon theme strings to actual tile frame arrays
 * Accepts any object with a theme property matching DungeonTheme
 */
export function resolveDungeonVisuals(dungeonData: { theme: DungeonTheme }): ResolvedDungeonVisuals {
  const { theme } = dungeonData;

  return {
    floorTiles: FLOOR_THEMES[theme.floor] || FLOOR_THEMES.stone,
    wallTilesH: WALL_THEMES_H[theme.wall] || WALL_THEMES_H.stone,
    wallTilesV: WALL_THEMES_V[theme.wall] || WALL_THEMES_V.stone,
    monsterFrame: MONSTER_MAPPING[theme.monster] || MONSTERS.ZOMBIE,
    bossFrame: MONSTER_MAPPING[theme.boss] || MONSTERS.DRAGON,
  };
}

/**
 * Resolve a prop type string to its frame and optional animation
 */
export function resolvePropConfig(propType: string): PropConfig | null {
  const normalized = propType.toLowerCase().replace(/\s+/g, '_');
  return PROP_CONFIGS[normalized] || null;
}

/**
 * Given a world tile frame number, return an animation key if this frame
 * represents an animatable prop (e.g., torch, candles)
 */
export function getPropAnimationFromFrame(frame: number): string | undefined {
  for (const key of Object.keys(PROP_CONFIGS)) {
    const cfg = PROP_CONFIGS[key];
    if (cfg.frame === frame) return cfg.animationKey;
  }
  return undefined;
}

/**
 * Get a random floor tile from a theme
 */
export function getRandomFloorTile(themeKey: string): number {
  const tiles = FLOOR_THEMES[themeKey] || FLOOR_THEMES.stone;
  return randomElement(tiles);
}

/**
 * Get a random horizontal wall tile from a theme
 */
export function getRandomWallTileH(themeKey: string): number {
  const tiles = WALL_THEMES_H[themeKey] || WALL_THEMES_H.stone;
  return randomElement(tiles);
}

/**
 * Get a random vertical wall tile from a theme
 */
export function getRandomWallTileV(themeKey: string): number {
  const tiles = WALL_THEMES_V[themeKey] || WALL_THEMES_V.stone;
  return randomElement(tiles);
}

/**
 * Get monster frame from type string
 */
export function getMonsterFrameByType(monsterType: string): number {
  return MONSTER_MAPPING[monsterType.toLowerCase()] || MONSTERS.ZOMBIE;
}
